///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthPosition } from '../position';
import { LinkFeatureView } from './view/link-feature-view';

import { GtthStepGroupEnum, SectorPolarityEnum } from '../../../../generated/shared-types.model';
import { GtthHistoryService } from '../../engine/history-service';
import { GtthLinkFeatureDtoModel } from '../../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { TopologyLinkTypeEnum } from '../../../../services/project-socket/dto/topology-types.model';
import { NodeFeature } from '../node/node-feature';

export const FREQUENCY_SET_AUTOMATICALLY = 0;

export class LinkFeature {
    public static readonly possibleLosStates: TopologyLinkTypeEnum[] = [
        TopologyLinkTypeEnum.NOT_CONFIRMED_LOS,
        TopologyLinkTypeEnum.NO_LOS,
        TopologyLinkTypeEnum.FIBER,
        TopologyLinkTypeEnum.CONFIRMED_LOS
    ];
    distance: number;
    protected id: number;
    protected used_prod_0: number;
    protected used_prod_1: number;
    protected used_ant0: number;
    protected used_ant1: number;
    protected to_remove: boolean;
    protected tx_freq: number;
    protected rx_freq: number;
    protected frequencyForced: boolean;
    protected is_active: boolean;
    protected is_installed: boolean;
    protected is_user_forced: boolean;
    protected is_disabled: boolean;
    protected sect_idx: number;
    protected sect_ap_idx: number;
    protected sect_dir: number;
    protected sector_in_device: number;
    protected link_type: number;
    protected color: string;
    protected assigned_name_0: string;
    protected assigned_name_1: string;
    protected node_0_ip_address: string;
    protected node_1_ip_address: string;

    protected p2mpReportSectorIndex: number;
    protected sectorPolarity: SectorPolarityEnum;
    private readonly viewObject: LinkFeatureView;
    private isLinkSelected = false;

    constructor(
        private readonly fromId: number,
        private readonly toId: number,
        public historyService: GtthHistoryService
    ) {
        this.id = LinkFeature.generateLinkId(fromId, toId);
        const fromPosition = this.historyService.logicalTopology.getStation(fromId).getPosition();
        const toPosition = this.historyService.logicalTopology.getStation(toId).getPosition();
        this.distance = google.maps.geometry.spherical.computeDistanceBetween(fromPosition.toLatLng(google), toPosition.toLatLng(google));
        this.viewObject = new LinkFeatureView(historyService, fromPosition, toPosition);
    }

    /**
     * Optimized pairing function by Matthew Szudzik (http://szudzik.com/ElegantPairing.pdf)
     * https://stackoverflow.com/a/13871379
     *
     * @return unique hash
     */
    public static generateLinkId(fromId: number, toId: number): number {
        return Math.max(fromId, toId) * Math.max(fromId, toId) + Math.min(fromId, toId);
    }

    static parseGeoJson(geoJson: GtthLinkFeatureDtoModel, historyService: GtthHistoryService): LinkFeature {
        const fromNodeId = geoJson.getNode0IdCustom();
        const toNodeId = geoJson.getNode1IdCustom();

        const link = new LinkFeature(fromNodeId, toNodeId, historyService);
        link.parseGeoJson(geoJson);
        return link;
    }

    parseGeoJson(geoJson: GtthLinkFeatureDtoModel) {
        this.used_prod_0 = geoJson.getUsedProd0();
        this.used_prod_1 = geoJson.getUsedProd1();
        this.used_ant0 = geoJson.getUsedAnt0();
        this.used_ant1 = geoJson.getUsedAnt1();
        this.to_remove = geoJson.getToRemove();
        this.tx_freq = geoJson.getTxFreq() !== undefined ? geoJson.getTxFreq() : 10;
        this.rx_freq = geoJson.getRxFreq() !== undefined ? geoJson.getRxFreq() : 10;
        this.frequencyForced = geoJson.isFrequencyForced();
        this.is_active = geoJson.getIsActive(); // default value ?
        this.is_installed = geoJson.getIsInstalled();
        this.is_user_forced = geoJson.getIsUserForced();
        this.is_disabled = geoJson.getIsDisabled();
        this.sect_idx = geoJson.getSectorIdx();
        this.p2mpReportSectorIndex = geoJson.getP2mpRepSectorIdx();
        this.sect_ap_idx = geoJson.getSectorApIdx();
        this.sect_dir = geoJson.getSectorDirection();
        this.sector_in_device = geoJson.getSectorInDevice();
        this.link_type = geoJson.getLinkType() !== undefined ? geoJson.getLinkType() : TopologyLinkTypeEnum.NOT_CONFIRMED_LOS;
        this.color = geoJson.getColor();
        this.assigned_name_0 = geoJson.getAssignedName0();
        this.assigned_name_1 = geoJson.getAssignedName1();
        this.node_0_ip_address = geoJson.getNode0IPAddress();
        this.node_1_ip_address = geoJson.getNode1IPAddress();
        this.sectorPolarity = geoJson.getSectorPolarity();
    }

    toGeoJson(): GtthLinkFeatureDtoModel {
        const linkFeature = new GtthLinkFeatureDtoModel(
            this.getFromId(),
            this.getToId(),
            this.getColor(),
            this.sect_idx,
            this.p2mpReportSectorIndex,
            this.sect_dir,
            this.sector_in_device,
            this.sect_ap_idx,
            [],
            this.tx_freq,
            this.rx_freq,
            this.used_prod_0,
            this.used_prod_1,
            this.used_ant0,
            this.used_ant1,
            this.link_type,
            this.assigned_name_0,
            this.assigned_name_1,
            this.node_0_ip_address,
            this.node_1_ip_address,
            this.sectorPolarity
        );

        linkFeature.setToRemove(this.to_remove);
        linkFeature.setFrequencyForced(this.frequencyForced);
        linkFeature.setTxFreq(this.tx_freq);
        linkFeature.setRxFreq(this.rx_freq);
        linkFeature.setIsActive(this.is_active);
        linkFeature.setIsInstalled(this.is_installed);
        linkFeature.setIsUserForced(this.is_user_forced);
        linkFeature.setIsDisabled(this.is_disabled);
        linkFeature.setSectorIdx(this.sect_idx);
        linkFeature.setP2mpRepSectorIdx(this.p2mpReportSectorIndex);
        linkFeature.setSectorApIdx(this.sect_ap_idx);
        linkFeature.setSectorDirection(this.sect_dir);

        return linkFeature;
    }

    getId(): number {
        return this.id;
    }

    isFromStation(stationId: number): boolean {
        return this.fromId === stationId;
    }

    isToStation(stationId: number): boolean {
        return this.toId === stationId;
    }

    belongToStation(stationId: number): boolean {
        return this.fromId === stationId || this.toId === stationId;
    }

    getFromId(): number {
        return this.fromId;
    }

    getToId(): number {
        return this.toId;
    }

    getFromStation(): NodeFeature {
        return this.historyService.logicalTopology.getStation(this.fromId);
    }

    getToStation(): NodeFeature {
        return this.historyService.logicalTopology.getStation(this.toId);
    }

    getP2mpRepSectorIndex(): number {
        return this.p2mpReportSectorIndex ?? this.sect_idx;
    }

    getSectorIndex(): number {
        return this.sect_idx;
    }

    setSectorIndex(index: number) {
        this.sect_idx = index;
    }

    getSectorApIndex(): number {
        return this.sect_ap_idx;
    }

    getSectorStationId(): number {
        const apIndex = this.getSectorApIndex();

        if (apIndex === 0) {
            return this.fromId;
        } else {
            return this.toId;
        }
    }

    getSectorStationName(): string {
        const apIndex = this.getSectorApIndex();

        if (apIndex === 0) {
            return this.getFromStation().getName();
        } else {
            return this.getToStation().getName();
        }
    }

    getNodeProduct(nodeId: number) {
        return this.fromId === nodeId ? this.used_prod_0 : this.used_prod_1;
    }

    getBuUsedProduct(): number {
        return this.getSectorApIndex() === 0 ? this.used_prod_0 : this.used_prod_1;
    }

    getTuUsedProduct(): number {
        return this.getSectorApIndex() === 0 ? this.used_prod_1 : this.used_prod_0;
    }

    isBuProductHasSectors() {
        return this.historyService.projectProductsService.isDeviceHasSectors(this.getBuUsedProduct());
    }

    setSectorApIndex(apIndex: number) {
        this.sect_ap_idx = apIndex;
    }

    getSectorDirectionAngle(): number {
        return this.sect_dir;
    }

    setSectorDirection(directionAngle: number) {
        this.sect_dir = directionAngle;
    }

    isActive(): boolean {
        return this.is_active;
    }

    isUserForced(): boolean {
        return this.is_user_forced;
    }

    isInstalled(): boolean {
        return this.is_installed;
    }

    getAssignedName0(): string {
        return this.assigned_name_0;
    }

    getAssignedName1(): string {
        return this.assigned_name_1;
    }

    setAssignedName0(asName: string): void {
        this.assigned_name_0 = asName;
    }

    setAssignedName1(asName: string): void {
        this.assigned_name_1 = asName;
    }

    getNode0IPAddress(): string {
        return this.node_0_ip_address;
    }

    getNode1IPAddress(): string {
        return this.node_1_ip_address;
    }

    setNode0IPAddress(ip0: string): void {
        this.node_0_ip_address = ip0;
    }

    setNode1IPAddress(ip1: string): void {
        this.node_1_ip_address = ip1;
    }

    public setLinkType(value: number) {
        if (
            this.historyService.selectedStepNumber !== GtthStepGroupEnum.PLAN_AND_OPTIMISE &&
            this.historyService.selectedStepNumber !== GtthStepGroupEnum.BOM
        ) {
            if (this.link_type && this.link_type === value) {
                return;
            }

            this.getFromStation().changeDraggable();
            this.getToStation().changeDraggable();

            this.link_type = value;

            this.viewObject.setLinkTrueStatus(this.getLinkType(true));
            this.viewObject.setLinkCurrentStatus(this.getLinkType());
            this.viewObject.refreshLink();
        }
    }

    public setLinkIsDisabled(value: boolean) {
        if (this.is_disabled && this.is_disabled === value) {
            return;
        }

        this.is_disabled = value;

        this.is_active = false;
        if (
            this.historyService.selectedStepNumber !== GtthStepGroupEnum.PLAN_AND_OPTIMISE &&
            this.historyService.selectedStepNumber !== GtthStepGroupEnum.BOM
        ) {
            this.viewObject.setIsDisabled(value);
            this.viewObject.setLinkTrueStatus(this.getLinkType(true));
            this.viewObject.setLinkCurrentStatus(this.getLinkType());
            this.viewObject.refreshLink();
        }

        this.historyService.interactiveWindowsService.linkDisabled(this.getFromStation().getName(), this.getToStation().getName(), value);
    }

    public setUserForced(isForced: boolean) {
        if (isForced === this.is_user_forced) {
            return;
        }

        this.is_user_forced = isForced;

        this.getFromStation().changeDraggable();
        this.getFromStation().refreshState();
        this.getToStation().changeDraggable();
        this.getToStation().refreshState();

        this.viewObject.setIsUserForced(isForced);

        this.updateLinkStatus();

        this.historyService.interactiveWindowsService.linkFrozen(this.getFromStation().getName(), this.getToStation().getName(), isForced);
    }

    setIsInstalled(isInstalled: boolean) {
        if (isInstalled === this.is_installed) {
            return;
        }

        this.is_installed = isInstalled;
        this.viewObject.setLinkTrueStatus(this.getLinkType(true));
        this.viewObject.setLinkCurrentStatus(this.getLinkType());
        this.viewObject.setIsInstalled(isInstalled);
        this.viewObject.refreshLink();

        this.historyService.interactiveWindowsService.linkInstalled(
            this.getFromStation().getName(),
            this.getToStation().getName(),
            isInstalled
        );
    }

    getLinkType(getTrueType = false): TopologyLinkTypeEnum {
        if (getTrueType || this.link_type === TopologyLinkTypeEnum.FIBER) {
            return this.link_type;
        }

        if (
            this.isUserForced() ||
            (this.historyService.selectedStepNumber !== GtthStepGroupEnum.LOCATIONS_AND_LOSES &&
                this.isActive() &&
                this.historyService.logicalTopology.isShowPlanningMode)
        ) {
            return TopologyLinkTypeEnum.LINK;
        } else {
            return this.link_type;
        }
    }

    getLinkIsDisabled(): boolean {
        return this.is_disabled;
    }

    getUsedProd0(): number {
        return this.used_prod_0;
    }

    setUsedProd0(value: number) {
        this.used_prod_0 = value;
    }

    getUsedProd1(): number {
        return this.used_prod_1;
    }

    setUsedProd1(value: number) {
        this.used_prod_1 = value;
    }

    getUsedAnt0(): number {
        return this.used_ant0;
    }

    setUsedAnt0(value: number) {
        this.used_ant0 = value;
    }

    getUsedAnt1(): number {
        return this.used_ant1;
    }

    setUsedAnt1(value: number) {
        this.used_ant1 = value;
    }

    getDistance(): number {
        return google.maps.geometry.spherical.computeDistanceBetween(
            this.getFromStation().getPosition().toLatLng(google),
            this.getToStation().getPosition().toLatLng(google)
        );
    }

    draw(): boolean {
        this.viewObject.setLinkName(`${this.getFromStation().getName()}-${this.getToStation().getName()}`);
        this.viewObject.setLinkTrueStatus(this.getLinkType(true));
        this.viewObject.setLinkCurrentStatus(this.getLinkType());
        this.viewObject.setIsSelected(false);
        this.viewObject.setIsDisabled(this.getLinkIsDisabled());
        this.viewObject.setIsUserForced(this.isUserForced());
        this.viewObject.setIsInstalled(this.isInstalled());
        this.viewObject.setColor(this.getColor());
        this.viewObject.setIsInActiveLayer(this.isInActiveLayer());
        return this.viewObject.draw();
    }

    hide() {
        this.destroyListeners();
        this.viewObject.hide();
    }

    refreshColor() {
        this.viewObject.setColor(this.getColor());
        this.viewObject.setIsInActiveLayer(this.isInActiveLayer());
        this.viewObject.refreshLink();
    }

    initListeners() {
        this.viewObject.registerClickListener(() => {
            this.historyService.ngZone.run(() => {
                if (!this.historyService.isCtrlKeyPressed) {
                    this.historyService.clearSelectionCloseWindowAndArea();
                } else {
                    this.historyService.closeInfoWindows();
                }
                this.historyService.logicalTopology.addSelectedLink(this);
            });
        });

        this.viewObject.registerRightClickListener((event: google.maps.MapMouseEvent) => {
            this.historyService.ngZone.run(() => {
                this.checkClickOnLink(GtthPosition.fromEventObject(event));
            });
        });
    }

    destroyListeners() {
        this.viewObject.destroyListeners();
    }

    select() {
        if (!this.historyService.isEditModeEnabled) {
            return;
        }

        this.isLinkSelected = true;
        this.viewObject.setLinkTrueStatus(this.getLinkType(true));
        this.viewObject.setLinkCurrentStatus(this.getLinkType());
        this.viewObject.setIsSelected(this.isLinkSelected);
        this.viewObject.refreshLink();

        this.historyService.interactiveWindowsService.linkSelected(this.getFromStation().getName(), this.getToStation().getName(), true);
    }

    unselect() {
        this.isLinkSelected = false;
        this.viewObject.setLinkTrueStatus(this.getLinkType(true));
        this.viewObject.setLinkCurrentStatus(this.getLinkType());
        this.viewObject.setIsSelected(this.isLinkSelected);
        this.viewObject.refreshLink();

        this.historyService.interactiveWindowsService.linkSelected(this.getFromStation().getName(), this.getToStation().getName(), false);
    }

    updateLinkStatus() {
        this.viewObject.setLinkTrueStatus(this.getLinkType(true));
        this.viewObject.setLinkCurrentStatus(this.getLinkType());
        this.viewObject.refreshLink();
    }

    isSelected(): boolean {
        return this.isLinkSelected;
    }

    showInfoWindow(position?: GtthPosition) {
        this.historyService.infoWindowsService.openLinkInfoWindow(this.getId(), position);
    }

    setTxFrequency(frequency: number): void {
        this.tx_freq = frequency;
    }

    getTxFrequency(): number {
        return this.tx_freq;
    }

    setRxFrequency(frequency: number): void {
        this.rx_freq = frequency;
    }

    getRxFrequency(): number {
        return this.rx_freq;
    }

    setFrequencyForced(forced: boolean): void {
        this.frequencyForced = forced;
    }

    isFrequencyForced(): boolean {
        return this.frequencyForced;
    }

    getColor(): string {
        return this.color;
    }

    //    bom

    public isInActiveLayer(): boolean {
        return (
            this.historyService.logicalTopology.activeLayerIdx === -1 ||
            this.historyService.logicalTopology.activeLayerIdx === this.getLinkLayerIndex()
        );
    }

    public getLinkLayerIndex(): number {
        return Math.min(this.getFromStation().getLayerIndex(), this.getToStation().getLayerIndex());
    }

    public getSectorInDevice(): number {
        return this.sector_in_device;
    }

    public getSectorPolarity(): SectorPolarityEnum {
        return this.sectorPolarity;
    }

    private checkClickOnLink(clickPosition: GtthPosition) {
        const fromPosition = this.getFromStation().getPosition();
        const toPosition = this.getToStation().getPosition();

        const minLat = Math.min(fromPosition.latitude, toPosition.latitude);
        const maxLat = Math.max(fromPosition.latitude, toPosition.latitude);
        const minLng = Math.min(fromPosition.longitude, toPosition.longitude);
        const maxLng = Math.max(fromPosition.longitude, toPosition.longitude);

        const clickLat = clickPosition.latitude;
        const clickLng = clickPosition.longitude;

        if (minLat < clickLat && maxLat > clickLat && minLng < clickLng && maxLng > clickLng) {
            this.showInfoWindow(clickPosition);
            return;
        }

        let newClickLat: number;
        let newClickLng: number;

        if (minLat < clickLat && maxLat > clickLat) {
            const [node1, node2] = this.getOrderedNodesByLatitude(fromPosition, toPosition);
            newClickLat = clickLat;
            newClickLng = this.calculateNewClickCoordinate(clickLat, node1.latitude, node2.latitude, node1.longitude, node2.longitude);
        } else if (minLng < clickLng && maxLng > clickLng) {
            const [node1, node2] = this.getOrderedNodesByLongitude(fromPosition, toPosition);
            newClickLng = clickLng;
            newClickLat = this.calculateNewClickCoordinate(clickLng, node1.longitude, node2.longitude, node1.latitude, node2.latitude);
        } else {
            newClickLng = (fromPosition.longitude + toPosition.longitude) / 2;
            newClickLat = (fromPosition.latitude + toPosition.latitude) / 2;
        }

        this.showInfoWindow(GtthPosition.fromValues(newClickLng, newClickLat));
    }

    private getOrderedNodesByLatitude(fromPosition: GtthPosition, toPosition: GtthPosition): [GtthPosition, GtthPosition] {
        return fromPosition.latitude < toPosition.latitude ? [fromPosition, toPosition] : [toPosition, fromPosition];
    }

    private getOrderedNodesByLongitude(fromPosition: GtthPosition, toPosition: GtthPosition): [GtthPosition, GtthPosition] {
        return fromPosition.longitude < toPosition.longitude ? [fromPosition, toPosition] : [toPosition, fromPosition];
    }

    private calculateNewClickCoordinate(
        clickCoord: number,
        coord1: number,
        coord2: number,
        otherCoord1: number,
        otherCoord2: number
    ): number {
        return otherCoord1 + ((clickCoord - coord1) * (otherCoord2 - otherCoord1)) / (coord2 - coord1);
    }
}
